import React, { useMemo, useState } from 'react';
import cx from 'classnames';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useDispatch, useSelector } from 'react-redux';
import DoublePage from 'components/Icons/DoublePage';
import UITooltip from 'components/Others/UITooltip';
import { CALL_CENTER_MAIL_BY_REGION, CALL_CENTER_PHONE_NUMBER_BY_REGION } from 'constants/callCenter';
import useCopyToClipboard from 'hooks/useCopyToClipboard';
import { clearNonNumeric } from 'utils/common.utils';
import footerData from '../../globalscripts/jsons/footerData.json';
import { RootState } from '../../redux/store';
import Facebook from '../Icons/Facebook';
import Instagram from '../Icons/Instagram';
import Linkedin from '../Icons/Linkedin';
import X from '../Icons/X';
import TextLink from '../TextLink/TextLink';
import { openAuth, setInitialAuthTab } from 'redux/features/auth/authSlice';
import ProductTag from 'components/Others/ProductTag';
import { getWelcomeCampaing } from 'constants/welcomeCampaing';
import MailIcon from 'components/Icons/MailIcon';
import PhoneIcon from 'components/Icons/PhoneIcon';
import TalkIcon from 'components/Icons/TalkIcon';
import FAQIcon from 'components/Icons/FAQIcon';
import DresdenLogo from 'components/Icons/DresdenLogo';
import Image from 'next/image';
import PlusFooter from 'components/Icons/PlusFooter';
import MinesFooter from 'components/Icons/MinesFooter';
import { useAppSelector } from 'redux/hooks';
import Cookies from 'js-cookie';
import { getSession } from 'functions/session';
import { startQueue } from 'redux/features/bag/bagSlice';
import { CREATE_USER_CART } from 'gqlStrings/mutations/addToCart';
import { useMutation } from '@apollo/client';

type RegionKey = 'international' | 'ca' | 'au' | 'nz';

interface Region {
  name: string;
  slug: string;
  currency: string;
  flag: {
    rounded: string;
    default: string;
  };
}

type AccordionState = {
  shop: boolean;
  ourGlasses: boolean;
  aboutUs: boolean;
  help: boolean;
};

export default function Footer() {
  const [mobileAccordion, setMobileAccordion] = useState<AccordionState>({
    shop: false,
    ourGlasses: false,
    aboutUs: false,
    help: false
  });

  const toggleAccordion = (section: keyof AccordionState) => {
    setMobileAccordion(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const AVAILABLE_REGIONS: Record<RegionKey, Region> = {
    au: {
      name: 'Australia',
      slug: 'au',
      currency: 'AUD',
      flag: {
        rounded: '/images/regions/rounded/au.svg',
        default: '/images/regions/default/au.jpg'
      }
    },
    ca: {
      name: 'Canada',
      slug: 'ca',
      currency: 'CAD',
      flag: {
        rounded: '/images/regions/rounded/ca.svg',
        default: '/images/regions/default/ca.jpg'
      }
    },
    nz: {
      name: 'New Zealand',
      slug: 'nz',
      currency: 'NZD',
      flag: {
        rounded: '/images/regions/rounded/nz.svg',
        default: '/images/regions/default/nz.jpg'
      }
    },
    international: {
      name: 'International',
      slug: 'international',
      currency: 'USD',
      flag: {
        rounded: '/images/regions/rounded/int.svg',
        default: '/images/regions/default/int.jpg'
      }
    }
  };

  const isLogin = useSelector((state: RootState) => state.authEvent.isLogin);
  const selectedRegion = useSelector(
    (state: RootState) => state.regionEvent.selectedRegion
  );
  const country = useSelector(
    (state: RootState) => state.regionEvent.country
  );
  const user = useSelector((state: RootState) => state.authEvent.user);
  const authToken = useSelector(
    (state: RootState) => state.authEvent.authToken
  );
  const [value, copy] = useCopyToClipboard();

  const dispatch = useDispatch();
  const router = useRouter();
  const regions = useAppSelector((state) => state.regionEvent.regions);
  const [totalExcute, setTotalExcute] = useState(false);
  const welcomeCampaign = useMemo(() => {
    return getWelcomeCampaing(router.locale!);
  }, [router]);

  const footerLinks = useMemo(() => {
    let links: any = [
      {
        id: 2,
        text: 'Stores',
        link: 'stores'
      },
      {
        id: 3,
        text: 'Browse Our Products',
        link: 'shop'
      },
      {
        id: 4,
        text: 'Shipping And Returns',
        link: 'our-glasses/shipping-and-returns'
      },
      {
        id: 5,
        text: 'Warranty',
        link: 'our-glasses/warranty'
      },
      {
        id: 6,
        text: welcomeCampaign.title,
        link: welcomeCampaign.slug
      },
      {
        id: 7,
        text: <>Referral Program</>,
        link: 'referral-program'
      },
      {
        id: 8,
        text: <>Upgrade to Blue Light Filter</>,
        link: 'upgrade-blue-light-filter'
      },
      {
        id: 9,
        text: <>Eyewear Selection</>,
        link: 'eyewear-selection'
      }
    ];

    if (!welcomeCampaign.active) {
      links = links.filter((link: any) => link.id !== 6);
    }
    return links;
  }, [footerData]);

  const [mutateCreateCart] = useMutation(CREATE_USER_CART, {
    context: { clientName: 'laravel' },
    onCompleted: () => {
      dispatch(startQueue());
    },
    onError: (error) => {
      console.log(error);
    }
  });

  function afterRemoveLocalStorage() {
    if (!totalExcute && getSession() === null) {
      if (isLogin) {
        mutateCreateCart({
          variables: {
            input: {
              isEmpty: true
            }
          }
        });
      } else {
        dispatch(startQueue());
      }
      setTotalExcute(true);
    }
  }

  const updateRegion = async (regionKey: RegionKey) => {
    if (regionKey !== selectedRegion.key) {
      Cookies.set('changeRegionFromModal', regionKey);
      const region = regions.find(
        (regionItem: any) => regionItem.key === regionKey
      );
      afterRemoveLocalStorage();

      if (region.key === 'international') {
        window.location.href = window.location.origin + '/' + router.asPath;
      } else {
        window.location.href = window.location.origin + '/' + region.key + router.asPath;
      }
    }
  };

  const currentYear = new Date().getFullYear();
  return (
    <>
      {!(router.asPath.indexOf('/contact-lenses') > -1) && (!router.asPath.includes('page')) && (!router.asPath.includes('referral-program')) && isLogin && (('order_count' in user && Number(user.order_count) == 0)) && welcomeCampaign.active && (
        <div className="flex flex-col border-y-[1px] gap-[20px] w-full py-[75px] border-[#ECECEC] border-b-2 border-t-2 text-brand-black100 mt-[50px]">
          <div className="flex flex-1 flex-col justify-center items-center gap-[10px]">
            <p className="text-[24px] leading-6 text-center"> Enjoy <span className='font-semibold'> {getWelcomeCampaing(router.locale?.toLowerCase() ?? 'au').discount}% off</span> on your <span className='font-semibold'>first purchase!</span> </p>
            <div className="flex mt-[10px] justify-between items-center bg-[#D9F3F7] rounded-md p-[16px] w-[278px]">
              <p className="text-brand-grey450"> Discount code: </p>
              <div className="flex items-center justify-between w-1/2">
                <p> {welcomeCampaign.code} </p>
                <UITooltip id="copy-tooltip" place='top' messageWrapperClassName='whitespace-pre' message={value === welcomeCampaign.code ? 'Copied!' : '  Copy  '}>
                  <span className="cursor-pointer" onClick={() => copy(welcomeCampaign.code)}> <DoublePage /> </span>
                </UITooltip>
              </div>
            </div>
          </div>
        </div>
      )}

      {!(router.asPath.indexOf('/contact-lenses') > -1) && !isLogin && (!router.asPath.includes('page')) && (!router.asPath.includes('referral-program')) && (
        <div className="flex flex-col border-y-[1px] gap-[20px] w-full py-[75px] border-[#ECECEC] border-b-2 border-t-2 text-brand-black100 mt-[50px]">
          <div className="flex justify-center sm:text-[22px] xsm:text-[18px] md:text-[24px] leading-7">
            {getWelcomeCampaing(router.locale?.toLowerCase() ?? 'au').active ? (
              <p className="px-[20px] text-center">Register today and get your <span className='font-semibold'>{getWelcomeCampaing(router.locale?.toLowerCase() ?? 'au').discount}% off on your first purchase!</span> </p>
            ) : (
              <p className="px-[20px] text-center">Register today!</p>
            )}
          </div>
          <div className='relative flex justify-center items-center gap-[8px] xsmx:w-full flex-row' >
            <button
              onClick={() => {
                dispatch(openAuth());
                dispatch(setInitialAuthTab('SIGN_UP'));
              }}
              className={cx(
                'flex w-[150px] h-[48px] items-center justify-center rounded-md px-[18px] text-[14px] bg-brand-black100 font-medium transition-colors w-full text-white border-[2px] border-brand-black100 bg-white text-center hover:bg-white hover:text-brand-black100',
              )}>
              Create Account
            </button>
          </div>
        </div>
      )}
      <footer className={cx("flex flex-col gap-[50px] w-full items-center justify-center pb-10 mdx:pb-8 pt-8 mdx:pt-0 bg-black",
        { 'pt-10': !(isLogin && (('order_count' in user && Number(user.order_count) == 0))) && welcomeCampaign.active })}>



        <div className="flex h-full w-full max-w-[1224px] flex-col px-4">
          <div className="mb-8 flex md:mb-28 items-start justify-between lgx:flex-col lgx:items-center">
            {footerData ? (
              <>
                {/* Desktop Layout */}
                <div className="hidden md:grid w-full grid-cols-2 items-start justify-between gap-[20px] lg:grid-cols-5">
                  <div className="grid grid-flow-row items-start gap-[15px]">
                    <DresdenLogo />
                    <div>
                      <ul className="flex gap-[8px] items-center w-full">
                        <li className="w-[38px] h-[38px]">
                          <Link href={
                            router.locale === 'ca'
                              ? 'https://www.facebook.com/dresdenvisioncanada'
                              : 'https://www.facebook.com/dresdenvision'
                          }
                            target="_blank"
                            aria-label="Go to Dresden Vision Facebook"
                          >
                            <Facebook />
                          </Link>
                        </li>
                        <li className="w-[38px] h-[38px]">
                          <Link
                            target="_blank"
                            className="w-[38px] h-[38px]"
                            aria-label="Go to Dresden Vision Twitter"
                            href="https://x.com/dresdenvision">
                            <X />
                          </Link>
                        </li>
                        <li className="w-[38px] h-[38px]">
                          <Link
                            target="_blank"
                            aria-label="Go to Dresden Vision Linkedin"
                            className="w-[38px] h-[38px]"
                            href="https://www.linkedin.com/company/dresdenvision/mycompany/">
                            <Linkedin />
                          </Link>
                        </li>
                        <li className="w-[38px] h-[38px]">
                          <Link
                            target="_blank"
                            aria-label="Go to Dresden Vision Instagram"
                            className="w-[38px] h-[38px]"
                            href={
                              router.locale === 'ca'
                                ? 'https://www.instagram.com/dresdenvisionca/'
                                : 'https://www.instagram.com/dresdenvision/'
                            }>
                            <Instagram />
                          </Link>
                        </li>
                      </ul>
                    </div>
                  </div>
                  <div className="grid grid-flow-row items-start gap-[15px]">
                    <h3 className="font-sans text-sm font-bold text-[#616161]">Shop</h3>
                    {footerLinks.map((item: any) => {
                      if (item.id == 10 && (router.locale == 'nz' || router.locale == 'international')) return null;
                      return (
                        <React.Fragment key={item.id}>
                          <div className="group flex items-center">
                            <Link prefetch={false} href={`/${item.link}`}>
                              <span
                                className={`flex items-center font-sans text-sm font-normal text-[#EAEAEA]`}>
                                {item.text}
                              </span>
                            </Link>
                          </div>
                          {item && item.text && item.text == 'Warranty' && (
                            <hr className='w-full border-[#2A2A2A] my-[12px]' />
                          )}
                        </React.Fragment>
                      )
                    })}
                  </div>
                  <div className="grid grid-flow-row items-start gap-[15px]">
                    <h3 className="font-sans text-sm font-bold text-[#616161]">
                      Our Glasses
                    </h3>
                    <div className="group flex items-center">
                      <Link prefetch={false} href={`/online-pupillary-distance`}>

                        <span
                          className={`flex items-center font-sans text-sm font-normal text-[#EAEAEA]`}>
                          {`Online Pupil Distance Measurement Tool`}
                        </span>

                      </Link>
                    </div>
                    {footerData
                      .filter((el: any) => el.category === 'our-glasses')
                      .map(
                        (item: any) =>
                          !footerLinks.filter(
                            (e: any) => e.text === item.text
                          )[0] && !item.text.includes('Shipping') && (
                            <div key={item.id} className="group flex items-center">
                              <Link prefetch={false} href={`/${item.link}`}>
                                <span
                                  className={`flex items-center font-sans text-sm font-normal text-[#EAEAEA]`}>
                                  {item.text}
                                </span>
                              </Link>
                            </div>
                          )
                      )}
                    {['ca', 'au'].includes(router.locale as string) && (
                      <div className="group flex items-center">
                        <Link prefetch={false} href="/eye-health/health-funds">

                          <span
                            className={`flex items-center font-sans text-sm font-normal text-[#EAEAEA]`}>
                            Health Funds
                          </span>

                        </Link>
                      </div>
                    )}

                    {['au'].includes(router.locale as string) && (
                      <div className="group flex items-center">
                        <Link prefetch={false} href="/hcf-nogap">
                          <span
                            className={`flex items-center font-sans text-sm font-normal text-[#EAEAEA]`}>
                            HCF No Gap
                          </span>
                        </Link>
                      </div>
                    )}

                    <div className="group flex items-center">
                      <Link prefetch={false} href="/eye-health/glossary">

                        <span
                          className={`flex items-center font-sans text-sm font-normal text-[#EAEAEA]`}>
                          Glossary
                        </span>

                      </Link>
                    </div>
                    <div className="group flex items-center">
                      <Link prefetch={false} href={['ca', 'nz'].includes(router.locale as string) ? '/progressives' : '/multifocals'}>
                        <span
                          className={`flex items-center font-sans text-sm font-normal text-[#EAEAEA]`}>
                          {['ca', 'nz'].includes(router.locale as string) ? 'Progressives' : 'Multifocals'}
                        </span>
                      </Link>
                    </div>

                    <div className="group flex items-center">
                      <Link prefetch={false} href="/bifocal-lenses">
                        <span
                          className={`flex items-center font-sans text-sm font-normal text-[#EAEAEA]`}>
                          Bifocal Lenses
                        </span>
                      </Link>
                    </div>

                    <div className="group flex items-center">
                      <Link prefetch={false} href="/single-vision-lenses">
                        <span
                          className={`flex items-center font-sans text-sm font-normal text-[#EAEAEA]`}>
                          Single Vision Lenses
                        </span>
                      </Link>
                    </div>
                  </div>

                  <div className="grid grid-flow-row items-start gap-[15px]">
                    <h3 className="font-sans text-sm font-bold text-[#616161]">
                      About Us
                    </h3>

                    <div className="group flex items-center">
                      <Link prefetch={false} href="/blog">

                        <span
                          className={`flex items-center font-sans text-sm font-normal text-[#EAEAEA]  `}>
                          Blog
                        </span>

                      </Link>
                    </div>
                    {footerData
                      .filter((el: any) => el.category === 'about-us')
                      .map(
                        (item: any) =>
                          (!footerLinks.filter(
                            (e: any) => e.text === item.text
                          )[0]) && item.text !== 'FAQ' && (
                            <div key={item.id} className="group flex items-center">
                              <Link prefetch={false} href={`/${item.link}`}>
                                <span
                                  className={`flex items-center font-sans text-sm font-normal text-[#EAEAEA]`}>
                                  {item.text}
                                </span>
                              </Link>
                            </div>
                          )
                      )}
                    {(router.locale === 'ca') && (
                      <div className="group flex items-center">
                        <Link prefetch={false} href="/career">
                          <span
                            className={`flex items-center font-sans text-sm font-normal text-[#EAEAEA]  `}>
                            Careers
                          </span>
                        </Link>
                      </div>)}

                  </div>
                  <div className="grid grid-flow-row items-start gap-[15px]">
                    <h3 className="font-sans text-sm font-bold text-[#616161]">
                      Help
                    </h3>

                    <p className='text-[#EAEAEA] text-sm font-normal'>Send us your questions and
                      our team will get back to you as quickly as possible.</p>
                    <div className="group flex items-center gap-[10px]">
                      <Link href="/contact-us" className="flex items-center font-sans text-sm font-normal cursor-pointer text-[#EAEAEA]" >
                        Contact Us
                      </Link>
                    </div>

                    <div className="group flex items-center gap-[10px]">
                      <MailIcon />
                      <a href={`mailto:${CALL_CENTER_MAIL_BY_REGION[router.locale as string]}`}>
                        <span className="flex items-center font-sans text-sm font-normal text-[#EAEAEA]">
                          {CALL_CENTER_MAIL_BY_REGION[router.locale as string]}
                        </span>
                      </a>
                    </div>

                    <div className="group flex items-center gap-[10px]">
                      <PhoneIcon />
                      <span className="flex items-center font-sans text-sm font-normal text-[#EAEAEA]">
                        <a href={'tel:' + clearNonNumeric(CALL_CENTER_PHONE_NUMBER_BY_REGION[router.locale as string] || '')}>
                          {CALL_CENTER_PHONE_NUMBER_BY_REGION[router.locale as string] || ''}
                        </a>
                      </span>
                    </div>

                    <div className="group flex items-center gap-[10px]">
                      <TalkIcon />
                      <a className="flex items-center font-sans text-sm font-normal cursor-pointer text-[#EAEAEA]" onClick={() => {
                        try {
                          if (window) {
                            //@ts-ignore
                            window.zE("messenger", "open")
                          }
                        } catch (e) {
                          console.log("zendesk not loaded")
                        }
                      }}>
                        Talk with an agent
                      </a>
                    </div>

                    <div className="group flex items-center gap-[10px]">
                      <FAQIcon />
                      <Link href="/about-us/faq" className="flex items-center font-sans text-sm font-normal cursor-pointer text-[#EAEAEA]" >
                        FAQ
                      </Link>
                    </div>
                  </div>
                </div>

                {/* Mobile Accordion Layout */}
                <div className="md:hidden w-full">
                  {/* Shop Accordion */}
                  <div>
                    <button
                      onClick={() => toggleAccordion('shop')}
                      className="flex w-full items-center justify-between py-4 text-left"
                    >
                      <h3 className="font-sans text-sm font-bold text-white">Shop</h3>
                      <span className={cx("text-[#EAEAEA] transition-transform duration-200", {
                        "rotate-180": mobileAccordion.shop
                      })}>
                        {mobileAccordion.shop ? <MinesFooter /> : <PlusFooter />}
                      </span>
                    </button>
                    <div className={cx("overflow-hidden transition-all duration-300", {
                      "max-h-0": !mobileAccordion.shop,
                      "max-h-96 pb-4": mobileAccordion.shop
                    })}>
                      <div className="grid grid-flow-row items-start gap-[15px]">
                        {footerLinks.map((item: any) => {
                          if (item.id == 10 && (router.locale == 'nz' || router.locale == 'international')) return null;
                          return (
                            <React.Fragment key={item.id}>
                              <div className="group flex items-center">
                                <Link prefetch={false} href={`/${item.link}`}>
                                  <span className="flex items-center font-sans text-sm font-normal text-[#EAEAEA]">
                                    {item.text}
                                  </span>
                                </Link>
                              </div>
                              {item && item.text && item.text == 'Warranty' && (
                                <hr className='w-full border-[#2A2A2A] my-[12px]' />
                              )}
                            </React.Fragment>
                          )
                        })}
                      </div>
                    </div>
                  </div>

                  {/* Our Glasses Accordion */}
                  <div className="border-t border-[#2A2A2A]">
                    <button
                      onClick={() => toggleAccordion('ourGlasses')}
                      className="flex w-full items-center justify-between py-4 text-left"
                    >
                      <h3 className="font-sans text-sm font-bold text-white">Our Glasses</h3>
                      <span className={cx("text-[#EAEAEA] transition-transform duration-200", {
                        "rotate-180": mobileAccordion.ourGlasses
                      })}>
                        {mobileAccordion.ourGlasses ? <MinesFooter /> : <PlusFooter />}
                      </span>
                    </button>
                    <div className={cx("overflow-hidden transition-all duration-300", {
                      "max-h-0": !mobileAccordion.ourGlasses,
                      "max-h-96 pb-4": mobileAccordion.ourGlasses
                    })}>
                      <div className="grid grid-flow-row items-start gap-[15px]">
                        <div className="group flex items-center">
                          <Link prefetch={false} href="/online-pupillary-distance">
                            <span className="flex items-center font-sans text-sm font-normal text-[#EAEAEA]">
                              Online Pupil Distance Measurement Tool
                            </span>
                          </Link>
                        </div>
                        {footerData
                          .filter((el: any) => el.category === 'our-glasses')
                          .map(
                            (item: any) =>
                              !footerLinks.filter(
                                (e: any) => e.text === item.text
                              )[0] && !item.text.includes('Shipping') && (
                                <div key={item.id} className="group flex items-center">
                                  <Link prefetch={false} href={`/${item.link}`}>
                                    <span className="flex items-center font-sans text-sm font-normal text-[#EAEAEA]">
                                      {item.text}
                                    </span>
                                  </Link>
                                </div>
                              )
                          )}
                        {['ca', 'au'].includes(router.locale as string) && (
                          <div className="group flex items-center">
                            <Link prefetch={false} href="/eye-health/health-funds">
                              <span className="flex items-center font-sans text-sm font-normal text-[#EAEAEA]">
                                Health Funds
                              </span>
                            </Link>
                          </div>
                        )}
                        {['au'].includes(router.locale as string) && (
                          <div className="group flex items-center">
                            <Link prefetch={false} href="/hcf-nogap">
                              <span className="flex items-center font-sans text-sm font-normal text-[#EAEAEA]">
                                HCF No Gap
                              </span>
                            </Link>
                          </div>
                        )}
                        <div className="group flex items-center">
                          <Link prefetch={false} href="/eye-health/glossary">
                            <span className="flex items-center font-sans text-sm font-normal text-[#EAEAEA]">
                              Glossary
                            </span>
                          </Link>
                        </div>
                        <div className="group flex items-center">
                          <Link prefetch={false} href={['ca', 'nz'].includes(router.locale as string) ? '/progressives' : '/multifocals'}>
                            <span className="flex items-center font-sans text-sm font-normal text-[#EAEAEA]">
                              {['ca', 'nz'].includes(router.locale as string) ? 'Progressives' : 'Multifocals'}
                            </span>
                          </Link>
                        </div>
                        <div className="group flex items-center">
                          <Link prefetch={false} href="/bifocal-lenses">
                            <span className="flex items-center font-sans text-sm font-normal text-[#EAEAEA]">
                              Bifocal Lenses
                            </span>
                          </Link>
                        </div>
                        <div className="group flex items-center">
                          <Link prefetch={false} href="/single-vision-lenses">
                            <span className="flex items-center font-sans text-sm font-normal text-[#EAEAEA]">
                              Single Vision Lenses
                            </span>
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* About Us Accordion */}
                  <div className="border-t border-[#2A2A2A]">
                    <button
                      onClick={() => toggleAccordion('aboutUs')}
                      className="flex w-full items-center justify-between py-4 text-left"
                    >
                      <h3 className="font-sans text-sm font-bold text-white">About Us</h3>
                      <span className={cx("text-[#EAEAEA] transition-transform duration-200", {
                        "rotate-180": mobileAccordion.aboutUs
                      })}>
                        {mobileAccordion.aboutUs ? <MinesFooter /> : <PlusFooter />}
                      </span>
                    </button>
                    <div className={cx("overflow-hidden transition-all duration-300", {
                      "max-h-0": !mobileAccordion.aboutUs,
                      "max-h-96 pb-4": mobileAccordion.aboutUs
                    })}>
                      <div className="grid grid-flow-row items-start gap-[15px]">
                        <div className="group flex items-center">
                          <Link prefetch={false} href="/blog">
                            <span className="flex items-center font-sans text-sm font-normal text-[#EAEAEA]">
                              Blog
                            </span>
                          </Link>
                        </div>
                        {footerData
                          .filter((el: any) => el.category === 'about-us')
                          .map(
                            (item: any) =>
                              (!footerLinks.filter(
                                (e: any) => e.text === item.text
                              )[0]) && item.text !== 'FAQ' && (
                                <div key={item.id} className="group flex items-center">
                                  <Link prefetch={false} href={`/${item.link}`}>
                                    <span className="flex items-center font-sans text-sm font-normal text-[#EAEAEA]">
                                      {item.text}
                                    </span>
                                  </Link>
                                </div>
                              )
                          )}
                        {(router.locale === 'ca') && (
                          <div className="group flex items-center">
                            <Link prefetch={false} href="/career">
                              <span className="flex items-center font-sans text-sm font-normal text-[#EAEAEA]">
                                Careers
                              </span>
                            </Link>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Help Accordion */}
                  <div className="border-t border-[#2A2A2A] border-b">
                    <button
                      onClick={() => toggleAccordion('help')}
                      className="flex w-full items-center justify-between py-4 text-left"
                    >
                      <h3 className="font-sans text-sm font-bold text-white">Help</h3>
                      <span className={cx("text-[#EAEAEA] transition-transform duration-200", {
                        "rotate-180": mobileAccordion.help
                      })}>
                        {mobileAccordion.help ? <MinesFooter /> : <PlusFooter />}
                      </span>
                    </button>
                    <div className={cx("overflow-hidden transition-all duration-300", {
                      "max-h-0": !mobileAccordion.help,
                      "max-h-96 pb-4": mobileAccordion.help
                    })}>
                      <div className="grid grid-flow-row items-start gap-[15px]">
                        <p className='text-[#EAEAEA] text-sm font-normal'>Send us your questions and
                          our team will get back to you as quickly as possible.</p>
                        <div className="group flex items-center gap-[10px]">
                          <Link href="/contact-us" className="flex items-center font-sans text-sm font-normal cursor-pointer text-[#EAEAEA]">
                            Contact Us
                          </Link>
                        </div>
                        <div className="group flex items-center gap-[10px]">
                          <MailIcon />
                          <a href={`mailto:${CALL_CENTER_MAIL_BY_REGION[router.locale as string]}`}>
                            <span className="flex items-center font-sans text-sm font-normal text-[#EAEAEA]">
                              {CALL_CENTER_MAIL_BY_REGION[router.locale as string]}
                            </span>
                          </a>
                        </div>
                        <div className="group flex items-center gap-[10px]">
                          <PhoneIcon />
                          <span className="flex items-center font-sans text-sm font-normal text-[#EAEAEA]">
                            <a href={'tel:' + clearNonNumeric(CALL_CENTER_PHONE_NUMBER_BY_REGION[router.locale as string] || '')}>
                              {CALL_CENTER_PHONE_NUMBER_BY_REGION[router.locale as string] || ''}
                            </a>
                          </span>
                        </div>
                        <div className="group flex items-center gap-[10px]">
                          <TalkIcon />
                          <a className="flex items-center font-sans text-sm font-normal cursor-pointer text-[#EAEAEA]" onClick={() => {
                            try {
                              if (window) {
                                //@ts-ignore
                                window.zE("messenger", "open")
                              }
                            } catch (e) {
                              console.log("zendesk not loaded")
                            }
                          }}>
                            Talk with an agent
                          </a>
                        </div>
                        <div className="group flex items-center gap-[10px]">
                          <FAQIcon />
                          <Link href="/about-us/faq" className="flex items-center font-sans text-sm font-normal cursor-pointer text-[#EAEAEA]">
                            FAQ
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            ) : null}
          </div>

          <div className='flex items-start md:flex-row flex-col md:gap-0 gap-3 mb-[32px] md:items-center '>
            {/* Desktop Layout - Original Order */}
            <div className="hidden md:flex items-center">
              {Object.values(AVAILABLE_REGIONS).map((region: any, index: number) => (
                <React.Fragment key={region.name}>
                  <div
                    className={cx('flex items-center gap-[8px] cursor-pointer px-4 py-2 rounded-full transition-all duration-200 hover:bg-[#2A2A2A] ', {
                      'bg-white shadow-sm': selectedRegion && region.slug === selectedRegion.key,
                      'bg-transparent': !selectedRegion || region.slug !== selectedRegion.key
                    })}

                    onClick={() => {
                      updateRegion(region.slug as RegionKey)
                    }}>
                    {selectedRegion && region.slug === selectedRegion.key && (
                      <Image
                        src={selectedRegion && region.slug === selectedRegion.key ? region.flag.default : region.flag.rounded}
                        alt={region.name}
                        width={20}
                        height={14}
                        className="rounded-sm"
                      />
                    )}
                    <span className={cx("text-[14px] font-medium whitespace-nowrap", {
                      'text-black': selectedRegion && region.slug === selectedRegion.key,
                      'text-[#EAEAEA]': !selectedRegion || region.slug !== selectedRegion.key
                    })}>
                      {region.name}
                    </span>
                  </div>
                  {selectedRegion && index < Object.values(AVAILABLE_REGIONS).length - 1 &&
                    !(selectedRegion && region.slug === selectedRegion.key) &&
                    !(selectedRegion && Object.values(AVAILABLE_REGIONS)[index + 1]?.slug === selectedRegion.key) && (
                      <div className="w-px h-6 bg-[#3A3A3A] mx-1"></div>
                    )}
                </React.Fragment>
              ))}
            </div>

            {/* Mobile Layout - Selected Region First */}
            <div className="md:hidden flex flex-col gap-3">
              {/* Selected Region */}
              {selectedRegion && Object.values(AVAILABLE_REGIONS).map((region: any) => (
                selectedRegion && region.slug === selectedRegion.key && (
                  <div
                    key={`selected-${region.name}`}
                    className={cx('flex items-center gap-[8px] cursor-pointer px-4 py-2 rounded-full transition-all duration-200 bg-white shadow-sm justify-center max-w-[140px]')}
                    onClick={() => {
                      updateRegion(region.slug as RegionKey)
                    }}>
                    <Image
                      src={region.flag.default}
                      alt={region.name}
                      width={20}
                      height={14}
                      className="rounded-sm"
                    />
                    <span className="text-[14px] font-medium whitespace-nowrap text-black">
                      {region.name}
                    </span>
                  </div>
                )
              ))}

              {/* Other Regions */}
              <div className="flex flex-wrap gap-3 justify-start">
                {Object.values(AVAILABLE_REGIONS).map((region: any, index: number) => (
                  !(selectedRegion && region.slug === selectedRegion.key) && (
                    <div
                      key={`other-${region.name}`}
                      className={cx('flex items-start gap-[8px] cursor-pointer ')}
                      onClick={() => {
                        updateRegion(region.slug as RegionKey)
                      }}>
                      <span className="text-[14px] font-medium whitespace-nowrap text-[#EAEAEA]">
                        {region.name}
                      </span>
                      {index < Object.values(AVAILABLE_REGIONS).length - 1 && (
                        <div className="w-px h-6 bg-[#3A3A3A] mx-1"></div>
                      )}
                    </div>
                  )
                ))}
              </div>
            </div>
          </div>


          <div className='md:hidden'>
            <div className="mb-8 flex flex-row items-center gap-4 justify-between">
              <DresdenLogo />
              <div>
                <ul className="flex gap-[8px] items-center justify-center w-full">
                  <li className="w-[38px] h-[38px]">
                    <Link href={
                      router.locale === 'ca'
                        ? 'https://www.facebook.com/dresdenvisioncanada'
                        : 'https://www.facebook.com/dresdenvision'
                    }
                      target="_blank"
                      aria-label="Go to Dresden Vision Facebook"
                    >
                      <Facebook />
                    </Link>
                  </li>
                  <li className="w-[38px] h-[38px]">
                    <Link
                      target="_blank"
                      className="w-[38px] h-[38px]"
                      aria-label="Go to Dresden Vision Twitter"
                      href="https://x.com/dresdenvision">
                      <X />
                    </Link>
                  </li>
                  <li className="w-[38px] h-[38px]">
                    <Link
                      target="_blank"
                      aria-label="Go to Dresden Vision Linkedin"
                      className="w-[38px] h-[38px]"
                      href="https://www.linkedin.com/company/dresdenvision/mycompany/">
                      <Linkedin />
                    </Link>
                  </li>
                  <li className="w-[38px] h-[38px]">
                    <Link
                      target="_blank"
                      aria-label="Go to Dresden Vision Instagram"
                      className="w-[38px] h-[38px]"
                      href={
                        router.locale === 'ca'
                          ? 'https://www.instagram.com/dresdenvisionca/'
                          : 'https://www.instagram.com/dresdenvision/'
                      }>
                      <Instagram />
                    </Link>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between mdx:flex-col mdx:items-start">
            <span className="select-none font-sans text-sm font-medium text-brand-grey mdx:mb-4">
              {currentYear} © Dresden Vision
            </span>
            <div className="flex md:items-center mdx:flex-col-reverse items-start">
              <ul className="flex items-center mdx:mb-8">
                <li className="ml-9 mdx:ml-0">
                  <TextLink
                    linkTo="privacy"
                    className="select-none font-sans text-sm font-medium text-brand-grey">
                    Privacy
                  </TextLink>
                </li>
                <li className="ml-9">
                  <TextLink
                    linkTo="terms-and-conditions"
                    className="select-none font-sans text-sm font-medium text-brand-grey">
                    Terms & Conditions
                  </TextLink>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
}